import type { CreateMediaUploadMutation } from "#app/lib/data";

type CreatePhotoUploadResult = CreateMediaUploadMutation["createMediaUpload"];

/** Outcome of the shared reserve→PUT step. `stage` distinguishes a failed
 * reservation (carries the server message) from a failed byte PUT, so callers
 * can pick the right toast. */
export type ReservedPhotoUpload =
  | { ok: true; photoId: string }
  | { ok: false; stage: "reserve"; message: string | null }
  | { ok: false; stage: "upload" };

/**
 * Shared tail of the two-step photo upload handshake: validate the
 * createMediaUpload result, PUT the display bytes to the presigned S3 URL, and
 * (best-effort) PUT a thumb variant. Both the avatar and note/media upload
 * hooks call this so the reserve→PUT contract — success discrimination, PUT
 * headers, and thumb best-effort semantics — lives in exactly one place.
 *
 * The thumb is best-effort: its PUT is kicked off alongside the display PUT and
 * a network REJECTION is swallowed (a missing/failed thumb just makes confirm
 * drop the variant and tiles fall back to the full bytes — it must not fail the
 * upload or orphan the already-PUT display object).
 *
 * `onProgress` (0–1) reports the DISPLAY byte PUT only — the thumb is tiny and
 * runs in parallel, and reserve/processing aren't byte-measurable. Reported via
 * XHR upload events (fetch exposes no upload progress).
 *
 * `signal` aborts the in-flight PUT (and thumb fetch). On abort the result is a
 * plain `stage: "upload"` failure — callers should check `signal.aborted` to
 * tell a user cancel apart from a real network failure (e.g. to skip a toast).
 * The reserved photo row left behind is reaped server-side as an orphan.
 */
export async function uploadReservedMedia(
  target: CreatePhotoUploadResult | undefined,
  display: { blob: Blob; contentType: string },
  thumb?: { blob: Blob; contentType: string } | null,
  onProgress?: (fraction: number) => void,
  signal?: AbortSignal,
): Promise<ReservedPhotoUpload> {
  if (!target || !("uploadUrl" in target)) {
    return {
      ok: false,
      stage: "reserve",
      message:
        target && "message" in target && target.message ? target.message : null,
    };
  }

  const thumbPut =
    thumb && target.thumbUploadUrl
      ? fetch(target.thumbUploadUrl, {
          method: "PUT",
          headers: { "Content-Type": thumb.contentType },
          body: thumb.blob,
          // `null` rather than `undefined`: `RequestInit.signal` is
          // `AbortSignal | null`, so null is the sanctioned "no abort".
          signal: signal ?? null,
        }).catch(() => null)
      : null;

  const uploaded = await putWithProgress(
    target.uploadUrl,
    display.blob,
    display.contentType,
    onProgress,
    signal,
  );
  if (!uploaded) return { ok: false, stage: "upload" };

  // Let the thumb settle (so it's in place before confirm stats it) but ignore
  // its outcome.
  if (thumbPut) await thumbPut;
  return { ok: true, photoId: target.media.id };
}

/**
 * PUT a blob to a presigned URL via XHR so `upload.onprogress` can report byte
 * progress (fetch has no upload-progress event). Resolves true on a 2xx,
 * false on any non-2xx / network error / abort — never rejects, so the caller
 * maps a falsy result to the same `stage: "upload"` failure the fetch path did.
 * An `AbortSignal` aborts the request in flight.
 */
function putWithProgress(
  url: string,
  blob: Blob,
  contentType: string,
  onProgress?: (fraction: number) => void,
  signal?: AbortSignal,
): Promise<boolean> {
  return new Promise((resolve) => {
    if (signal?.aborted) {
      resolve(false);
      return;
    }
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url);
    xhr.setRequestHeader("Content-Type", contentType);
    if (onProgress) {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) onProgress(e.loaded / e.total);
      };
    }
    xhr.onload = () => {
      if (onProgress) onProgress(1);
      resolve(xhr.status >= 200 && xhr.status < 300);
    };
    xhr.onerror = () => resolve(false);
    xhr.onabort = () => resolve(false);
    signal?.addEventListener("abort", () => xhr.abort(), { once: true });
    xhr.send(blob);
  });
}
